/**
 * BASE SERVICE - Base Architecture
 * 
 * Clase abstracta base para todos los services
 */

import type { z } from 'zod';
import { validate } from '../utils/validation';
import { NotFoundError } from '../utils/errors';
import { Logger } from '@boosting/core';

export abstract class BaseService<T extends Record<string, any> = Record<string, any>, CreateDTO = Partial<T>, UpdateDTO = Partial<T>> {
  protected logger: Logger;

  constructor(
    protected collectionName: string,
    protected db: any, // Firestore instance
    protected createSchema?: z.ZodSchema<CreateDTO>,
    protected updateSchema?: z.ZodSchema<UpdateDTO>
  ) {
    this.logger = Logger.getLogger(`${collectionName}Service`);
  }

  /**
   * Get by ID
   */
  async getById(id: string): Promise<T | null> {
    try {
      const doc = await this.db.collection(this.collectionName).doc(id).get();
      
      if (!doc.exists) {
        return null;
      }
      
      return {
        id: doc.id,
        ...doc.data()
      } as T;
    } catch (error) {
      this.logger.error(`Error getting ${this.collectionName}/${id}`, error as Error, { id });
      throw error;
    }
  }

  /**
   * Create
   */
  async create(data: CreateDTO, customId?: string): Promise<T> {
    try {
      // Validate if schema provided
      const validatedData = this.createSchema 
        ? validate(this.createSchema, data)
        : data;

      const docData = {
        ...validatedData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      let docRef;
      if (customId) {
        docRef = this.db.collection(this.collectionName).doc(customId);
        await docRef.set(docData);
      } else {
        docRef = await this.db.collection(this.collectionName).add(docData);
      }

      this.logger.info(`Created ${this.collectionName}`, { id: customId || docRef.id });

      return {
        id: customId || docRef.id,
        ...docData
      } as unknown as T;
    } catch (error) {
      this.logger.error(`Error creating ${this.collectionName}`, error as Error, { data });
      throw error;
    }
  }

  /**
   * Update
   */
  async update(id: string, data: UpdateDTO): Promise<void> {
    try {
      // Validate if schema provided
      const validatedData = this.updateSchema
        ? validate(this.updateSchema, data)
        : data;

      // ⚠️ CRÍTICO: Firestore no acepta valores undefined
      // Filtrar cualquier campo con valor undefined antes de guardar
      const cleanedData = this.removeUndefinedValues({
        ...validatedData,
        updatedAt: new Date().toISOString()
      });

      await this.db.collection(this.collectionName).doc(id).update(cleanedData);
      
      this.logger.info(`Updated ${this.collectionName}`, { id });
    } catch (error) {
      this.logger.error(`Error updating ${this.collectionName}/${id}`, error as Error, { id, data });
      throw error;
    }
  }

  /**
   * Remove undefined values from object (Firestore doesn't accept undefined)
   */
  private removeUndefinedValues(obj: Record<string, any>): Record<string, any> {
    const cleaned: Record<string, any> = {};
    
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        // Recursively clean nested objects
        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
          cleaned[key] = this.removeUndefinedValues(value);
        } else {
          cleaned[key] = value;
        }
      }
    }
    
    return cleaned;
  }

  /**
   * Delete
   */
  async delete(id: string): Promise<void> {
    try {
      await this.db.collection(this.collectionName).doc(id).delete();
      this.logger.info(`Deleted ${this.collectionName}`, { id });
    } catch (error) {
      this.logger.error(`Error deleting ${this.collectionName}/${id}`, error as Error, { id });
      throw error;
    }
  }

  /**
   * Query with filters
   */
  protected async query(
    filters: Array<[string, any, any]> = [],
    options: {
      limit?: number;
      orderBy?: string;
      orderDirection?: 'asc' | 'desc';
      startAfter?: any;
    } = {}
  ): Promise<T[]> {
    try {
      let query = this.db.collection(this.collectionName) as any;

      // Apply filters
      filters.forEach(([field, operator, value]) => {
        query = query.where(field, operator, value);
      });

      // Apply ordering
      if (options.orderBy) {
        query = query.orderBy(options.orderBy, options.orderDirection || 'asc');
      }

      // Apply pagination
      if (options.startAfter) {
        query = query.startAfter(options.startAfter);
      }

      if (options.limit) {
        query = query.limit(options.limit);
      }

      const snapshot = await query.get();

      return snapshot.docs.map((doc: any) => ({
        id: doc.id,
        ...doc.data()
      })) as T[];
    } catch (error) {
      this.logger.error(`Error querying ${this.collectionName}`, error as Error, { filters, options });
      throw error;
    }
  }

  /**
   * Get all (use with caution)
   */
  async getAll(limit = 100): Promise<T[]> {
    return this.query([], { limit });
  }

  /**
   * Count documents
   */
  async count(filters: Array<[string, any, any]> = []): Promise<number> {
    const results = await this.query(filters);
    return results.length;
  }

  /**
   * Exists
   */
  async exists(id: string): Promise<boolean> {
    const doc = await this.getById(id);
    return doc !== null;
  }

  /**
   * Assert exists (throws if not found)
   */
  async assertExists(id: string): Promise<T> {
    const doc = await this.getById(id);
    if (!doc) {
      throw new NotFoundError(this.collectionName, id);
    }
    return doc;
  }
}

